/**
 * Коды справочников → тексты интерфейса.
 *
 * Backend отдаёт только коды (`plug_in_hybrid`, `lower_mileage`, `critical`), а
 * пользовательский текст живёт во фронтовых словарях — то же правило, что и для
 * объяснений подбора (CLAUDE.md §3 п. 6). Переводчик передаётся параметром:
 * модуль остаётся чистым и проверяется без React.
 *
 * Часть кодов уже переведена для каталога (`catalog.fuel.*`, `catalog.bodyType.*`,
 * `vehicle.feature.*`) — они переиспользуются, а не дублируются под новыми ключами.
 */

import type { TFunction } from 'i18next';

import type {
  BodyType,
  CriteriaPriority,
  EmphasisLevel,
  FeatureCode,
  FuelType,
  PriorityCode,
  ScoreComponent,
  UsageScenario,
} from './types';

/**
 * Переводчик передаётся параметром, а не берётся из хука: модуль остаётся
 * чистым. Тип — сам `TFunction`: свой упрощённый алиас не совпал бы с
 * перегрузками i18next и потребовал бы приведения на каждом вызове.
 */
export type Translate = TFunction;

export const bodyTypeKey = (value: BodyType): string => `catalog.bodyType.${value}`;
export const fuelKey = (value: FuelType): string => `catalog.fuel.${value}`;
export const featureKey = (value: FeatureCode): string => `vehicle.feature.${value}`;
export const usageKey = (value: UsageScenario): string => `criteria.usage.${value}`;
export const priorityKey = (value: PriorityCode): string => `criteria.priority.${value}`;
export const componentKey = (value: ScoreComponent): string => `criteria.component.${value}`;
export const emphasisKey = (value: EmphasisLevel): string => `criteria.emphasis.${value}`;

/**
 * Немецкое название земли по коду ISO 3166-2:DE.
 *
 * Зеркало статического справочника backend (`criteria/geography.py`), как и
 * `types.ts` — зеркало Pydantic-схем: генератора из OpenAPI в проекте пока нет
 * (SKILLS S-15). Названия земель не переводятся: «Nordrhein-Westfalen» — имя
 * собственное, и в русском интерфейсе оно остаётся тем же.
 */
export const GERMAN_STATE_NAMES: Readonly<Record<string, string>> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

/** Название земли или сам код, если справочники разошлись: код честнее пустоты. */
export const stateName = (code: string): string => GERMAN_STATE_NAMES[code] ?? code;

/**
 * Короткая тема вопроса («Регион», «Бюджет») для строки «осталось уточнить».
 *
 * Целиком вопрос туда не годится: он написан как реплика в диалоге и в панели
 * читается как оборванная фраза.
 */
export const questionTopicKey = (questionCode: string): string =>
  `chat.topics.${questionCode.toLowerCase()}`;

/** Приоритеты перечисляются в порядке рангов: первый — самый важный. */
export function orderedPriorities(priorities: readonly CriteriaPriority[]): CriteriaPriority[] {
  return [...priorities].sort((left, right) => left.rank - right.rank);
}
